import "@aws-amplify/ui-react/styles.css";
import {
  Button,
  Center,
  Container,
  Flex,
  Loader,
  MantineProvider,
  Paper,
  Title,
} from "@mantine/core";
import "@mantine/core/styles.css";
import "@mantine/dates/styles.css";
import "mantine-datatable/styles.css";
import { ModalsProvider } from "@mantine/modals";
import { Notifications } from "@mantine/notifications";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { fetchAuthSession, signInWithRedirect } from "aws-amplify/auth";
import { useCallback, useEffect, useState } from "react";
import { AdminRouter } from "@/admin/routers/AdminRouter";
import { idTokenToUserInfo, type JwtToken, UserContext } from "@/UserContext";
import { theme } from "../theme";
import { AdminSideNav } from "./components/AdminSideNav";

const SignInView = () => {
  const handleSignIn = async () => {
    await signInWithRedirect({
      provider: {
        custom: "GoogleWorkspace",
      },
    });
  };

  return (
    <Container size="600px" py="xl">
      <Paper withBorder p="xl">
        <Center>
          <div style={{ textAlign: "center" }}>
            <Title order={2} ta="center">
              Woofapy Admin Login
            </Title>
            <p
              style={{ color: "var(--mantine-color-dimmed)", margin: "1rem 0" }}
            >
              Please sign in to access the admin panel.
            </p>
            <Button onClick={handleSignIn} size="lg">
              Sign In
            </Button>
          </div>
        </Center>
      </Paper>
    </Container>
  );
};

const AdminContent = () => {
  const [idToken, setIdToken] = useState<JwtToken | undefined>();
  const [isLoading, setIsLoading] = useState(true);
  const userInfo = idToken && idTokenToUserInfo(idToken.payload);

  const checkAuthSession = useCallback(async () => {
    try {
      const authSession = await fetchAuthSession();
      setIdToken(authSession.tokens?.idToken);
    } catch (_error) {
      setIdToken(undefined);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuthSession();
  }, [checkAuthSession]);

  if (isLoading) {
    return (
      <Center style={{ height: "100vh" }}>
        <Loader size="lg" />
      </Center>
    );
  }

  if (!userInfo) {
    return <SignInView />;
  }

  return (
    <UserContext.Provider value={userInfo}>
      <Flex mih="100vh">
        <AdminSideNav />
        <Container
          fluid
          style={{
            flex: 1,
            padding: "2rem",
            backgroundColor: "var(--mantine-color-gray-0)",
          }}
        >
          <Paper withBorder p="xl" style={{ minHeight: "calc(100vh - 4rem)" }}>
            <AdminRouter />
          </Paper>
        </Container>
      </Flex>
    </UserContext.Provider>
  );
};

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: false,
    },
  },
});

export default function AdminApp() {
  return (
    <QueryClientProvider client={queryClient}>
      <MantineProvider theme={theme}>
        <ModalsProvider>
          <Notifications position="top-center" limit={3} />
          <AdminContent />
        </ModalsProvider>
      </MantineProvider>
    </QueryClientProvider>
  );
}
